import {Async, isAsyncServerError, isLoaded} from 'Core/models/Async';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {renderAsyncView} from 'Shared/components/RenderAsync';
import {A360CommissionsSectionError, A360CommissionsSectionNoRecordsFound, LoadingSpinner} from '../Placeholders';
import {EarnedActivityReport, OrdinaryActivityReport} from '../types';
import {ColumnConfig, CommissionsReportTable, renderStyledAmount} from './CommissionReportTable';
import {
  CommissionsReport,
  CommissionsReportData,
  CommissionsReportType,
  getCommissionsReportData,
} from 'Agent/Commissions/ducks/a360CommissionsReport';
import {A360CommissionSection} from '../CommissionSection';
import {formatCurrency} from 'Shared/utilities/FormatMoney';

const TOTAL_COMMISSIONS_EARNED_KEY = 'Total Commissions Earned';

const EarnedActivityReportView = () => {
  const {commissionsReportData} = useSelector((state: ReducerState) => state.a360CommissionsReport);

  const columns: ColumnConfig<EarnedActivityReport>[] = [
    {
      label: 'Product Type',
      fieldName: 'productType',
    },
    {
      label: 'Month to Date 1st Year',
      fieldName: 'monthToDateFirstYear',
      render: renderStyledAmount,
    },
    {
      label: 'Month to Date Renewal',
      fieldName: 'monthToDateRenewal',
      render: renderStyledAmount,
    },
    {
      label: 'Year to Date 1st year',
      fieldName: 'yearToDateFirstYear',
      render: renderStyledAmount,
    },
    {
      label: 'Year to Date Renewal',
      fieldName: 'yearToDateRenewal',
      render: renderStyledAmount,
    },
  ];

  return (
    <A360CommissionsReport<EarnedActivityReport>
      columns={columns}
      commissionsReportDataAsync={commissionsReportData as Async<EarnedActivityReport[]>}
      reportTitle="Earned Activity"
    />
  );
};

const isEarnedActivityReportArray = (data: any): data is EarnedActivityReport[] => 
  Array.isArray(data) && data.length > 0 && typeof(data[0] as EarnedActivityReport).productType === 'string';


const EarnedActivityReportSummaryView = () => {
  const {commissionsReportData} = useSelector((state: ReducerState) => state.a360CommissionsReport);

  return (
    <A360CommissionSection
      title="Earned Account Balance Summary"
      align="left"
      className="a360_commissions_reporting__earned-account-balances-summary"
      aria-label="earned account balance summary"
    >
      {isAsyncServerError(commissionsReportData) ? (
        <A360CommissionsSectionError error={commissionsReportData.errorDetails} />
      ) : (
        renderAsyncView(
          commissionsReportData,
          data => {
            if (!isEarnedActivityReportArray(data)) {
              return <A360CommissionsSectionNoRecordsFound />;
            }

            const totalCommissionsEarned = data.find(item => item.productType === TOTAL_COMMISSIONS_EARNED_KEY);
            return data.length > 0 && totalCommissionsEarned ? (
              <table className="a360_commissions_reporting__table-layout">
                <tbody>
                  <tr>
                    <td>Total Month to Date</td>
                    <td>{renderStyledAmount(totalCommissionsEarned.monthToDateRenewal)}</td>
                  </tr>
                  <tr>
                    <td>Total Year to Date</td>
                    <td>{renderStyledAmount(totalCommissionsEarned.yearToDateRenewal)}</td>
                  </tr>
                  <tr>
                    <td>Year to Date Interest</td>
                    <td>{renderStyledAmount('0')}</td>
                  </tr>
                </tbody>
              </table>
            ) : (
              <A360CommissionsSectionNoRecordsFound />
            );
          },
          LoadingSpinner
        )
      )}
    </A360CommissionSection>
  );
};

const A360CommissionsReport = <T extends CommissionsReportType>({
  columns,
  commissionsReportDataAsync,
  reportTitle,
}: {
  columns: ColumnConfig<T>[];
  commissionsReportDataAsync: Async<T[]>;
  reportTitle: string;
  className?: string;
}) => {
  return (
    <A360CommissionSection
      title={reportTitle}
      align="left"
      className="a360_commissions_reporting__report-table"
      aria-label="commissions report data"
    >
      {isAsyncServerError(commissionsReportDataAsync) ? (
        <A360CommissionsSectionError error={commissionsReportDataAsync.errorDetails} />
      ) : (
        renderAsyncView(
          commissionsReportDataAsync,
          commissionsReportData =>
            Array.isArray(commissionsReportData) && commissionsReportData?.length > 0 ? (
              <CommissionsReportTable
                columns={columns}
                data={commissionsReportData.filter(item => item.productType !== TOTAL_COMMISSIONS_EARNED_KEY)}
              />
            ) : (
              <A360CommissionsSectionNoRecordsFound />
            ),
          LoadingSpinner
        )
      )}
    </A360CommissionSection>
  );
};

const OrdinaryActivityReportView = () => {
  const {commissionsReportData} = useSelector((state: ReducerState) => state.a360CommissionsReport);

  const columns: ColumnConfig<OrdinaryActivityReport>[] = [
    {
      label: 'Description',
      fieldName: 'description',
    },
    {
      label: 'Month to Date Number of Policies',
      fieldName: 'mtdPolicyCount',
    },
    {
      label: 'Month to Date Annual Premium',
      fieldName: 'mtdTotalAnnualPremium',
      render: renderStyledAmount,
    },
    {
      label: 'Year to Date Number of Policies',
      fieldName: 'ytdPolicyCount',
    },
    {
      label: 'Year to Date Annual Premium',
      fieldName: 'ytdTotalAnnualPremium',
      render: renderStyledAmount,
    },
  ];

  return (
    <A360CommissionsReport<OrdinaryActivityReport>
      columns={columns}
      commissionsReportDataAsync={commissionsReportData as Async<OrdinaryActivityReport[]>}
      reportTitle="Ordinary Activity"
    />
  );
};

export const CommissionsReportToRender = () => {
  const {
    filter: {selectedReport},
  } = useSelector((state: ReducerState) => state.a360CommissionsReport);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getCommissionsReportData());
  }, []);
  switch (selectedReport) {
    case CommissionsReport.EARNED_ACTIVITY:
      return (
        <>
          <EarnedActivityReportView />
          <EarnedActivityReportSummaryView />
        </>
      );
    case CommissionsReport.ORDINARY:
      return (
        <>
          <OrdinaryActivityReportView />
        </>
      );
    default:
      return <A360CommissionsSectionNoRecordsFound />;
  }
};
